import React, { useMemo } from 'react'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  AIDraftStatus,
  AssignmentSummary,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { AIStatusBadge, DigestType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiStatusBadge/AIStatusBadge'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Display } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/display/Display'
import { StyledMarkdown } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/styledMarkdown/StyledMarkdown'
import { BadgeShape } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { DisplaySize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

interface AssignmentAISummaryTextProps {
  clientIntakeSummaryResponse: AssignmentSummary
}

const SummaryContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '100%',
  padding: `0 ${spacing['16px']}`,
  marginTop: spacing['16px'],
}))

const SummaryHeadingContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  gap: spacing['16px'],
  marginBottom: spacing['16px'],
}))

const SummaryTextContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingBottom: spacing['16px'],
}))

const AISummaryPreface = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  color: colors.textSecondary,
  marginBottom: spacing['16px'],
}))

const AssignmentAISummaryText: React.FC<AssignmentAISummaryTextProps> = ({ clientIntakeSummaryResponse }) => {
  const { colors } = useTheme()

  const clientIntakeAISummary = useMemo(() => {
    if (
      clientIntakeSummaryResponse?.status === AIDraftStatus.IN_PROGRESS ||
      clientIntakeSummaryResponse?.status === AIDraftStatus.NOT_STARTED
    ) {
      return 'Processing your summary now - this usually takes just a few minutes. You can read the full details below first and check back shortly.'
    } else if (
      clientIntakeSummaryResponse?.status === AIDraftStatus.ERROR ||
      clientIntakeSummaryResponse?.status === AIDraftStatus.NOT_FOUND
    ) {
      return 'Summary not found'
    } else if (clientIntakeSummaryResponse?.status === AIDraftStatus.COMPLETE) {
      return clientIntakeSummaryResponse.summary_text
    }
    return undefined
  }, [clientIntakeSummaryResponse])
  return (
    <SummaryContainer testID={tID('Assignments-ai-summary-container')}>
      <SummaryHeadingContainer testID={tID('Assignments-ai-summary-header-container')}>
        <Display size={DisplaySize.SMALL} text='Summary' />

        {clientIntakeSummaryResponse && (
          <View testID={tID('Assignments-ai-summary-status-badge-container')}>
            <AIStatusBadge
              digestType={DigestType.SUMMARY}
              status={clientIntakeSummaryResponse?.status}
              customText='AI GENERATED'
              shape={BadgeShape.RECT}
            />
          </View>
        )}
      </SummaryHeadingContainer>

      {clientIntakeSummaryResponse && clientIntakeSummaryResponse?.status === AIDraftStatus.COMPLETE && (
        <AISummaryPreface>
          <BodyText
            color={colors.textSecondary}
            text={`This summary was generated by AI based on the client's intake responses. It may contain incomplete or inaccurate information; it is your responsibility to review the full client intake before your first session`}
          />
        </AISummaryPreface>
      )}

      <SummaryTextContainer>
        <StyledMarkdown content={clientIntakeAISummary} customStyles={{ h1: { fontSize: 16 } }} />
      </SummaryTextContainer>
    </SummaryContainer>
  )
}

export default AssignmentAISummaryText
